import * as uuid from 'uuid';

import { getElementHumanName, getElementPreview } from '@editor2/services/elementService';
import { type Conflict, type ReorderConflict, ConflictType } from '@editor2/types/conflict';
import { type CourseState } from '@editor2/types/courseState';
import { NgTraditional } from '@editor2/types/ngTraditional';
import {
	getChapterByPageGroupId,
	getPageByElementId,
	getParentEntityByPageId
} from '../CourseChangesDiff/helpers';
import { type ElementType, ElementConfigName } from '@editor2/types/pageElements';
import { getEntityStringByNgType } from '@editor2/services/conflictResolutionService';
import { type Chapter } from '@editor2/types/chapter';
import { type PageGroup } from '@editor2/types/pageGroup';
import { type Page } from '@editor2/types/page';
import { getPageNumberIfExistsAndNotSpace } from '@editor2/services/pagesService';
import { getPageOrdinalNumber, getChapterOrdinalNumber } from '@editor2/services/numberingService';

export const sanitizeChildName = (attribute: string) => {
	return attribute[0].toUpperCase() + attribute.slice(1, -1);
};

const getElementName = ({ courseState, attribute, id }) => {
	const initialElement = courseState.initialCourse[attribute].entities[id];
	const currentElement = courseState[attribute].entities[id];
	const previewText = initialElement && getElementPreview(initialElement);

	return `${initialElement?.type || currentElement?.type}${previewText ? ` ${previewText}` : ''}`;
};

export const getChildName = ({ courseState, attribute, id }) => {
	return `${sanitizeChildName(attribute)}: ${
		courseState[attribute].entities[id]?.name ||
		courseState.initialCourse[attribute].entities[id]?.name ||
		(attribute === 'elements' && getElementName({ courseState, attribute, id }))
	}`;
};

export const getValue = (obj, attr) => {
	if (!obj) return;
	return typeof obj[attr] !== 'string' ? JSON.stringify(obj[attr]) : obj[attr];
};

export const getEntityName = (
	entityId: string,
	entityType: NgTraditional | ElementType,
	state: CourseState
) => {
	const entityName = entityType.split('::').pop();
	const entityString = getEntityStringByNgType(entityType);

	switch (true) {
		case entityType === NgTraditional.Book:
			return entityName;

		case entityType === NgTraditional.Chapter: {
			const chapterEntity = (state[entityString].entities[entityId] ||
				state.initialCourse[entityString].entities[entityId]) as Chapter;

			const chapterNumber =
				chapterEntity.chapter_number ||
				getChapterOrdinalNumber(state.chapters, chapterEntity.family_id);

			return `${entityName} ${chapterNumber}: ${chapterEntity.name}`;
		}

		case entityType === NgTraditional.PageGroup: {
			return `${entityName} ${
				(state[entityString].entities[entityId] as PageGroup)?.name ||
				(state.initialCourse[entityString].entities[entityId] as PageGroup)?.name
			}`;
		}

		case entityType === NgTraditional.Page: {
			const pageEntity = (state[entityString].entities[entityId] ||
				state.initialCourse[entityString].entities[entityId]) as Page;

			const pageNumber =
				getPageNumberIfExistsAndNotSpace(pageEntity) ||
				`#${getPageOrdinalNumber(state, pageEntity.family_id)}`;

			return `${entityName} ${pageNumber}: ${pageEntity.name}`;
		}

		case !!ElementConfigName[entityType]: {
			const parentPage =
				getPageByElementId(state.pages, entityId) ||
				getPageByElementId(state.initialCourse.pages, entityId);
			const pageNumber =
				getPageNumberIfExistsAndNotSpace(parentPage) ||
				`#${getPageOrdinalNumber(state, parentPage.family_id)}`;

			const element =
				state.elements.entities[entityId] || state.initialCourse.elements.entities[entityId];

			const elementHumanName = getElementHumanName(state.config, parentPage.page_type, element);
			return `Page ${pageNumber}: ${elementHumanName}`;
		}

		default:
			break;
	}
};

export const createReorderConflict = (conflict: Conflict, attribute: string) => {
	const reorderConflict: ReorderConflict = {
		conflictId: uuid.v4(),
		conflictType: ConflictType.ChildrenReorder,
		entityId: conflict.entityId,
		entityType: conflict.entityType,
		attribute: attribute,
		initial: conflict.initial && conflict.initial[attribute],
		current: conflict.current && conflict.current[attribute],
		incoming: conflict.incoming && conflict.incoming[attribute]
	};

	return reorderConflict;
};

export const getParentByEntityString = ({ state, entityString, childId }) => {
	switch (entityString) {
		case 'page_groups':
			return getChapterByPageGroupId(state.chapters, childId);

		case 'pages':
			return (
				getParentEntityByPageId(state.chapters, childId) ||
				getParentEntityByPageId(state.page_groups, childId)
			);

		case 'elements':
			return getPageByElementId(state.pages, childId);

		default:
			break;
	}

	return null;
};
